import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS, es, fr, pt, zhCN } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

const LOCALES = { it, en: enUS, es, fr, pt, zh: zhCN };

const parseYmd = (str) => {
  if (!str) return undefined;
  const [y, m, d] = String(str).split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const toYmd = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Selettore data moderno e touch-friendly (Popover + Calendar).
 * Sostituisce gli input nativi `type="date"`: celle grandi, ben cliccabili,
 * intestazioni localizzate. `value`/`onChange` usano il formato 'YYYY-MM-DD'.
 */
export const DatePicker = ({
  value,
  onChange,
  min,
  max,
  placeholder,
  className = '',
  'data-testid': testId,
  buttonClassName = '',
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = LOCALES[i18n.language] || enUS;

  const selected = useMemo(() => parseYmd(value), [value]);
  const minDate = useMemo(() => parseYmd(min), [min]);
  const maxDate = useMemo(() => parseYmd(max), [max]);

  const disabled = [];
  if (minDate) disabled.push({ before: minDate });
  if (maxDate) disabled.push({ after: maxDate });

  const label = selected
    ? format(selected, 'dd MMM yyyy', { locale })
    : (placeholder || t('common.selectDate', { defaultValue: 'Seleziona data' }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          className={cn(
            'h-11 justify-start gap-2 rounded-xl font-medium',
            !selected && 'text-muted-foreground',
            buttonClassName,
            className
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
          <span className="capitalize">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl" align="start" data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar
          mode="single"
          locale={locale}
          selected={selected}
          defaultMonth={selected || minDate || new Date()}
          disabled={disabled.length ? disabled : undefined}
          onSelect={(d) => { if (d) { onChange(toYmd(d)); setOpen(false); } }}
          className="p-4"
          classNames={{
            caption_label: 'text-base font-semibold capitalize',
            nav_button: 'h-9 w-9 rounded-lg bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-muted',
            head_cell: 'text-muted-foreground rounded-md w-11 font-medium text-xs uppercase',
            cell: 'relative p-0 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:rounded-lg',
            day: 'h-11 w-11 p-0 font-normal rounded-lg text-base aria-selected:opacity-100 hover:bg-primary/10 transition-colors',
            day_selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground shadow-sm',
            day_today: 'bg-accent text-accent-foreground font-semibold ring-1 ring-primary/30',
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
